import ReactApexChart from 'react-apexcharts';
import { withNamespaces } from 'react-i18next';
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap';

import useSupplierOverviewPackagingMaterialOverview from './hooks/useSupplierOverviewPackagingMaterialOverview';

const normalizeColor = (color) => {
    if (!color) {
        return color;
    }

    return color.startsWith('#') ? color : `#${color}`;
};

const PackagingMaterialOverviewSkeleton = () => (
    <Card className="overview-packaging-material-overview h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '30%' }} />
            <Row className="g-4 align-items-center">
                <Col md="6">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '280px' }} />
                </Col>
                <Col md="6">
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '24px' }} />
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '24px' }} />
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '24px' }} />
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '24px' }} />
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '24px' }} />
                </Col>
            </Row>
        </CardBody>
    </Card>
);

const PackagingMaterialOverview = ({
    t,
    supplierId,
}) => {
    const fetchPackagingMaterialOverviewQuery = useSupplierOverviewPackagingMaterialOverview({
        supplierId,
        t,
    });

    if (
        fetchPackagingMaterialOverviewQuery.isLoading
        || fetchPackagingMaterialOverviewQuery.isFetching
    ) {
        return <PackagingMaterialOverviewSkeleton />;
    }

    const items = fetchPackagingMaterialOverviewQuery.data?.items || [];
    const total = fetchPackagingMaterialOverviewQuery.data?.total
        ?? items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

    const series = [];
    const labels = [];
    const colors = [];

    items.forEach((item) => {
        series.push(Number(item.amount) || 0);
        labels.push(t(item.title));
        colors.push(normalizeColor(item.color));
    });

    const isSeriesEmpty = !series.length || series.every((value) => value === 0);

    const chartOptions = {
        labels: isSeriesEmpty ? [t('No Data')] : labels,
        colors: isSeriesEmpty ? ['#e0e0e0'] : colors,
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#828282',
                            offsetY: -4,
                        },
                        value: {
                            show: true,
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#1D1D1D',
                            offsetY: 8,
                            formatter: (val) => val,
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: t('Total'),
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#828282',
                            formatter: () => (isSeriesEmpty ? '0' : String(total)),
                        },
                    },
                },
            },
        },
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false,
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (val) => val,
            },
        },
        stroke: {
            width: 0,
        },
    };

    return (
        <Card className="overview-packaging-material-overview h-100 mb-0">
            <CardBody>
                <CardTitle tag="h6" className="overview-section-title mb-4">
                    {t('Packaging Material Overview')}
                </CardTitle>

                <Row className="g-4 align-items-center">
                    <Col xs="12" md="6" lg="5">
                        <div className="overview-packaging-material-overview__chart">
                            <ReactApexChart
                                options={chartOptions}
                                series={isSeriesEmpty ? [1] : series}
                                type="donut"
                                height="260px"
                            />
                        </div>
                    </Col>

                    <Col xs="12" md="6" lg="7">
                        <ul className="overview-packaging-material-overview__legend list-unstyled mb-0">
                            {items.map((item) => (
                                <li
                                    key={item.key || item.title}
                                    className="overview-packaging-material-overview__legend-item"
                                >
                                    <span className="overview-packaging-material-overview__legend-label">
                                        <span
                                            className="overview-packaging-material-overview__legend-swatch"
                                            style={{ backgroundColor: normalizeColor(item.color) }}
                                        />
                                        <span className="overview-packaging-material-overview__legend-name">
                                            {t(item.title)}
                                        </span>
                                    </span>
                                    <span className="overview-packaging-material-overview__legend-percent">
                                        {`${Number(item.percent) || 0}%`}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(PackagingMaterialOverview);
